import React from 'react';
import { useTranslation } from 'react-i18next';

const DEBIT_NATURE = ['Asset', 'Expense'];
const CREDIT_NATURE = ['Liability', 'Equity', 'Revenue'];

export default function LedgerTable({ data }) {
  const { t } = useTranslation();

  const rows = (data?.data || []).map((item, index) => ({
    ...item,
    index: index + 1
  }));

  const calculateTotals = () => {
    let totalOpeningDebit = 0;
    let totalOpeningCredit = 0;
    let totalOldBalanceDebit = 0;
    let totalOldBalanceCredit = 0;
    let totalMovementDebit = 0;
    let totalMovementCredit = 0;
    let totalClosingDebit = 0;
    let totalClosingCredit = 0;

    rows.forEach(row => {
      if ((row.level || 0) !== 0) return;

      const openingDebit = Number(row.opening_debit ?? 0);
      const openingCredit = Number(row.opening_credit ?? 0);
      const oldBalanceDebit = Number(row?.old_debit ?? row.total_debit ?? 0);
      const oldBalanceCredit = Number(row?.old_credit ?? row.total_credit ?? 0);
      const movementDebit = Number(row.month_debit ?? 0);
      const movementCredit = Number(row.month_credit ?? 0);

      totalOpeningDebit += openingDebit;
      totalOpeningCredit += openingCredit;
      totalOldBalanceDebit += oldBalanceDebit;
      totalOldBalanceCredit += oldBalanceCredit;
      totalMovementDebit += movementDebit;
      totalMovementCredit += movementCredit;

      let closingDebit = 0;
      let closingCredit = 0;

      if (DEBIT_NATURE.includes(row.account_type)) {
        const net =
          openingDebit + oldBalanceDebit + movementDebit - (openingCredit + oldBalanceCredit + movementCredit);
        closingDebit = net >= 0 ? net : 0;
        closingCredit = net < 0 ? Math.abs(net) : 0;
      } else if (CREDIT_NATURE.includes(row.account_type)) {
        const net =
          openingCredit + oldBalanceCredit + movementCredit - (openingDebit + oldBalanceDebit + movementDebit);
        closingCredit = net >= 0 ? net : 0;
        closingDebit = net < 0 ? Math.abs(net) : 0;
      }

      totalClosingDebit += closingDebit;
      totalClosingCredit += closingCredit;
    });

    return {
      totalOpeningDebit: totalOpeningDebit.toFixed(2),
      totalOpeningCredit: totalOpeningCredit.toFixed(2),
      totalOldBalanceDebit: totalOldBalanceDebit.toFixed(2),
      totalOldBalanceCredit: totalOldBalanceCredit.toFixed(2),
      totalMovementDebit: totalMovementDebit.toFixed(2),
      totalMovementCredit: totalMovementCredit.toFixed(2),
      totalClosingDebit: totalClosingDebit.toFixed(2),
      totalClosingCredit: totalClosingCredit.toFixed(2)
    };
  };

  const totals = calculateTotals();

  return (
    <div className='overflow-x-auto shadow-sm rounded-lg border border-gray-200'>
      <table className='w-full text-sm text-left text-gray-800'>
        <thead className='text-md font-semibold text-gray-900 bg-gray-300'>
          <tr>
            <th rowSpan={2} className='px-4 py-3  text-center'>
              {t('no')}
            </th>
            <th rowSpan={2} className='px-4 py-3  text-left'>
              {t('Account Code')}
            </th>
            <th rowSpan={2} className='px-4 py-3  text-left min-w-[200px]'>
              {t('Account Name')}
            </th>
            <th rowSpan={2} className='px-4 py-3  text-center'>
              {t('Type')}
            </th>
            <th colSpan={2} className='px-4 py-2 border border-gray-400 border-t-0 text-center bg-gray-300 px-2'>
              {t('Opening')}
            </th>
            <th
              colSpan={2}
              className='px-4 py-2 border border-gray-400 border-t-0 text-center bg-gray-300  text-center bg-gray-300 px-2'
            >
              {t('Old Balance')}
            </th>
            <th
              colSpan={2}
              className='px-4 py-2 border border-gray-400 border-t-0 text-center bg-gray-300 text-center bg-gray-300 px-2'
            >
              {t('Movement')}
            </th>
            <th
              colSpan={2}
              className='px-4 py-2 text-center border border-gray-400 border-t-0 text-center bg-gray-300 bg-gray-300 px-2'
            >
              {t('Closing')}
            </th>
          </tr>

          {/* Second row for Debit/Credit under each category */}
          <tr className='border-b border-gray-200'>
            <th className='px-3 py-2 border border-gray-400 border-t-0 text-center font-semibold bg-gray-300'>
              {t('Debit')}
            </th>
            <th className='px-3 py-2 border border-gray-400 border-t-0 text-center font-semibold bg-gray-300'>
              {t('Credit')}
            </th>
            <th className='px-3 py-2 border border-gray-400 border-t-0 text-center font-semibold bg-gray-300'>
              {t('Debit')}
            </th>
            <th className='px-3 py-2 border border-gray-400 border-t-0 text-center font-semibold bg-gray-300'>
              {t('Credit')}
            </th>
            <th className='px-3 py-2 border border-gray-400 border-t-0 text-center font-semibold bg-gray-300'>
              {t('Debit')}
            </th>
            <th className='px-3 py-2 border border-gray-400 border-t-0 text-center font-semibold bg-gray-300'>
              {t('Credit')}
            </th>
            <th className='px-3 py-2 border border-gray-400 border-t-0 text-center font-semibold bg-gray-300'>
              {t('Debit')}
            </th>
            <th className='px-3 py-2 border border-gray-400 border-t-0 text-center font-semibold bg-gray-300'>
              {t('Credit')}
            </th>
          </tr>
        </thead>

        <tbody className='divide-y divide-gray-200'>
          {rows.map((row, idx) => {
            const openingDebit = Number(row.opening_debit ?? 0);
            const openingCredit = Number(row.opening_credit ?? 0);
            const oldBalanceDebit = Number(row?.old_debit ?? row.total_debit ?? 0);
            const oldBalanceCredit = Number(row?.old_credit ?? row.total_credit ?? 0);
            const movementDebit = Number(row.month_debit ?? 0);
            const movementCredit = Number(row.month_credit ?? 0);

            let closingDebit = 0;
            let closingCredit = 0;

            if (DEBIT_NATURE.includes(row.account_type)) {
              const net =
                openingDebit + oldBalanceDebit + movementDebit - (openingCredit + oldBalanceCredit + movementCredit);
              closingDebit = net >= 0 ? net : 0;
              closingCredit = net < 0 ? Math.abs(net) : 0;
            } else if (CREDIT_NATURE.includes(row.account_type)) {
              const net =
                openingCredit + oldBalanceCredit + movementCredit - (openingDebit + oldBalanceDebit + movementDebit);
              closingCredit = net >= 0 ? net : 0;
              closingDebit = net < 0 ? Math.abs(net) : 0;
            }

            const isParent = row.children && row.children.length > 0;

            return (
              <tr
                key={row.account_id}
                className={`
                  transition-colors duration-150 border-b border-gray-300 text-gray-900
                  ${isParent ? 'bg-gray-200 ' : 'bg-white'}
                  ${row.level === 0 ? 'border-t border-t-gray-400' : ''}
                `}
              >
                <td className='px-4 py-2 border-r border-gray-300 text-center'>{row.index}</td>
                <td className='px-4 py-2 text-left'>{row.account_code}</td>
                <td className='relative border-r border-gray-300 text-left p-0'>
                  <div className='absolute inset-y-0 left-0 flex pointer-events-none'>
                    {Array.from({ length: row.level || 0 }).map((_, i) => (
                      <div key={i} className='w-6 border-r border-gray-500 h-full'></div>
                    ))}
                  </div>
                  <div
                    className='flex items-center py-2 pr-4'
                    style={{ paddingLeft: `${(row.level || 0) * 1.5 + 1}rem` }}
                  >
                    {isParent && <span className='mr-2 text-gray-600 text-[10px]'>▼</span>}
                    <span className='truncate'>{row.account_name}</span>
                  </div>
                </td>
                <td className='px-4 py-2 border-r border-gray-300 text-center'>{t(row.account_type)}</td>

                <td className='px-3 py-2 border-r border-gray-300 text-right whitespace-nowrap'>
                  {openingDebit.toFixed(2)}{' '}
                </td>
                <td className='px-3 py-2 border-r border-gray-300 text-right whitespace-nowrap'>
                  {openingCredit.toFixed(2)}{' '}
                </td>

                <td className='px-3 py-2 border-r border-gray-300 text-right whitespace-nowrap'>
                  {oldBalanceDebit.toFixed(2)}{' '}
                </td>
                <td className='px-3 py-2 border-r border-gray-300 text-right whitespace-nowrap'>
                  {oldBalanceCredit.toFixed(2)}{' '}
                </td>

                <td className='px-3 py-2 border-r border-gray-300 text-right whitespace-nowrap'>
                  {movementDebit.toFixed(2)}{' '}
                </td>
                <td className='px-3 py-2 border-r border-gray-300 text-right whitespace-nowrap'>
                  {movementCredit.toFixed(2)}{' '}
                </td>

                <td className='px-3 py-2 border-r border-gray-300 text-right whitespace-nowrap'>
                  {closingDebit.toFixed(2)}{' '}
                </td>
                <td className='px-3 py-2 text-right whitespace-nowrap'>{closingCredit.toFixed(2)} </td>
              </tr>
            );
          })}
        </tbody>

        {/* <tfoot className='bg-gray-100 font-semibold border-t border-gray-300 text-gray-900'>
          <tr>
            <td colSpan={4} className='px-4 py-3 text-right uppercase text-gray-700 border-r border-gray-300'>{t('Total')}</td>
            <td className='px-3 py-3 text-right border-r border-gray-300 whitespace-nowrap'>{totals.totalOpeningDebit} <span className='text-[10px] font-normal text-gray-500'>{t('SAR')}</span></td>
            <td className='px-3 py-3 text-right border-r border-gray-300 whitespace-nowrap'>{totals.totalOpeningCredit} <span className='text-[10px] font-normal text-gray-500'>{t('SAR')}</span></td>
            <td className='px-3 py-3 text-right border-r border-gray-300 whitespace-nowrap'>{totals.totalOldBalanceDebit} <span className='text-[10px] font-normal text-gray-500'>{t('SAR')}</span></td>
            <td className='px-3 py-3 text-right border-r border-gray-300 whitespace-nowrap'>{totals.totalOldBalanceCredit} <span className='text-[10px] font-normal text-gray-500'>{t('SAR')}</span></td>
            <td className='px-3 py-3 text-right border-r border-gray-300 whitespace-nowrap'>{totals.totalMovementDebit} <span className='text-[10px] font-normal text-gray-500'>{t('SAR')}</span></td>
            <td className='px-3 py-3 text-right border-r border-gray-300 whitespace-nowrap'>{totals.totalMovementCredit} <span className='text-[10px] font-normal text-gray-500'>{t('SAR')}</span></td>
            <td className='px-3 py-3 text-right border-r border-gray-300 whitespace-nowrap'>{totals.totalClosingDebit} <span className='text-[10px] font-normal text-gray-500'>{t('SAR')}</span></td>
            <td className='px-3 py-3 text-right whitespace-nowrap'>{totals.totalClosingCredit} <span className='text-[10px] font-normal text-gray-500'>{t('SAR')}</span></td>
          </tr>
        </tfoot>  */}
        <tfoot className='bg-gray-100 font-semibold border-t border-gray-300 text-gray-900'>
          <tr>
            <td colSpan={4} className='px-4 py-3 text-right uppercase text-gray-700 border-r border-gray-300'>{t('Total')}</td>
            <td className='px-3 py-3 text-right border-r border-gray-300 whitespace-nowrap'>{totals.totalOpeningDebit} <span className='text-[10px] font-normal text-gray-500'></span></td>
            <td className='px-3 py-3 text-right border-r border-gray-300 whitespace-nowrap'>{totals.totalOpeningCredit} <span className='text-[10px] font-normal text-gray-500'></span></td>
            <td className='px-3 py-3 text-right border-r border-gray-300 whitespace-nowrap'>{totals.totalOldBalanceDebit} <span className='text-[10px] font-normal text-gray-500'></span></td>
            <td className='px-3 py-3 text-right border-r border-gray-300 whitespace-nowrap'>{totals.totalOldBalanceCredit} <span className='text-[10px] font-normal text-gray-500'></span></td>
            <td className='px-3 py-3 text-right border-r border-gray-300 whitespace-nowrap'>{totals.totalMovementDebit} <span className='text-[10px] font-normal text-gray-500'></span></td>
            <td className='px-3 py-3 text-right border-r border-gray-300 whitespace-nowrap'>{totals.totalMovementCredit} <span className='text-[10px] font-normal text-gray-500'></span></td>
            <td className='px-3 py-3 text-right border-r border-gray-300 whitespace-nowrap'>{totals.totalClosingDebit} <span className='text-[10px] font-normal text-gray-500'></span></td>
            <td className='px-3 py-3 text-right whitespace-nowrap'>{totals.totalClosingCredit} <span className='text-[10px] font-normal text-gray-500'></span></td>
          </tr>
        </tfoot>  
      </table>
    </div>
  );
}
